import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  Timestamp,
} from 'firebase/firestore'
import { app } from './firebase'
import { getUserProfile } from './userProfile'
import { Difficulty } from '../hooks/gameReducer'

const db = getFirestore(app)

// Room types
export interface Room {
  id: string
  name: string
  creatorId: string
  members: string[]
  inviteCode: string
  createdAt: Timestamp
  maxMembers: number
  isActive: boolean
  isPrivate: boolean
}

export interface RoomMember {
  userId: string
  displayName: string
  joinedAt: Timestamp
}

export interface RoomRanking {
  userId: string
  displayName: string
  weeklyScore: number
  dailyPoints: number
  totalGames: number
  lastUpdated: Timestamp
}

export interface DailyVote {
  voterId: string
  votedForId: string
  date: string // YYYY-MM-DD format
  createdAt: Timestamp
}

// Generate a random invite code
export function generateInviteCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let code = ''
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

/**
 * Create a new room
 */
export async function createRoom(
  name: string,
  creatorId: string,
  maxMembers: number = 20,
  isPrivate: boolean = false
): Promise<Room> {
  try {
    const roomRef = doc(collection(db, 'rooms'))
    const inviteCode = generateInviteCode()

    const room: Room = {
      id: roomRef.id,
      name,
      creatorId,
      members: [creatorId],
      inviteCode,
      createdAt: Timestamp.now(),
      maxMembers,
      isActive: true,
      isPrivate,
    }

    await setDoc(roomRef, room)
    return room
  } catch (error) {
    console.error('Error creating room:', error)
    throw error
  }
}

/**
 * Get a room by invite code (without joining)
 */
export async function getRoomByInviteCode(
  inviteCode: string
): Promise<Room | null> {
  try {
    const roomsRef = collection(db, 'rooms')
    const q = query(
      roomsRef,
      where('inviteCode', '==', inviteCode),
      where('isActive', '==', true),
      limit(1)
    )

    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return null
    }

    return snapshot.docs[0].data() as Room
  } catch (error) {
    console.error('Error getting room by invite code:', error)
    return null
  }
}

/**
 * Join a room by invite code
 */
export async function joinRoomByCode(
  inviteCode: string,
  userId: string
): Promise<Room | null> {
  try {
    const roomsRef = collection(db, 'rooms')
    const q = query(
      roomsRef,
      where('inviteCode', '==', inviteCode),
      where('isActive', '==', true),
      limit(1)
    )

    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return null
    }

    const roomDoc = snapshot.docs[0]
    const room = roomDoc.data() as Room

    // Check if room is full
    if (room.members.length >= room.maxMembers) {
      throw new Error('Room is full')
    }

    // Check if user is already a member
    if (room.members.includes(userId)) {
      return room
    }

    // Add user to room
    await updateDoc(doc(db, 'rooms', room.id), {
      members: arrayUnion(userId),
    })

    return { ...room, members: [...room.members, userId] }
  } catch (error) {
    console.error('Error joining room:', error)
    throw error
  }
}

/**
 * Get room by ID
 */
export async function getRoom(roomId: string): Promise<Room | null> {
  try {
    const roomRef = doc(db, 'rooms', roomId)
    const roomSnap = await getDoc(roomRef)

    if (!roomSnap.exists()) {
      return null
    }

    return roomSnap.data() as Room
  } catch (error) {
    console.error('Error getting room:', error)
    throw error
  }
}

/**
 * Get all rooms for a user
 */
export async function getUserRooms(userId: string): Promise<Room[]> {
  try {
    const roomsRef = collection(db, 'rooms')
    const q = query(
      roomsRef,
      where('members', 'array-contains', userId),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    )

    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => doc.data() as Room)
  } catch (error) {
    console.error('Error getting user rooms:', error)
    throw error
  }
}

/**
 * Leave a room
 */
export async function leaveRoom(roomId: string, userId: string): Promise<void> {
  try {
    const roomRef = doc(db, 'rooms', roomId)
    await updateDoc(roomRef, {
      members: arrayRemove(userId),
    })
  } catch (error) {
    console.error('Error leaving room:', error)
    throw error
  }
}

/**
 * Get room rankings
 */
export async function getRoomRankings(
  roomId: string,
  difficulty: Difficulty = 5
): Promise<RoomRanking[]> {
  try {
    const difficultyPath = `rankings_${difficulty}`
    const rankingsRef = collection(db, 'rooms', roomId, difficultyPath)
    const q = query(rankingsRef, orderBy('weeklyScore', 'desc'), limit(50))

    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => doc.data() as RoomRanking)
  } catch (error) {
    console.error('Error getting room rankings:', error)
    throw error
  }
}

/**
 * Update room ranking for a user
 */
export async function updateRoomRanking(
  roomId: string,
  userId: string,
  scoreToAdd: number,
  difficulty: Difficulty = 5,
  pointsToAdd: number = 0
): Promise<void> {
  try {
    // Get display name from user profile
    const profile = await getUserProfile(userId)
    const displayName = profile?.displayName || 'Anonim'

    const difficultyPath = `rankings_${difficulty}`
    const rankingRef = doc(db, 'rooms', roomId, difficultyPath, userId)
    const rankingSnap = await getDoc(rankingRef)

    if (rankingSnap.exists()) {
      const current = rankingSnap.data() as RoomRanking
      await updateDoc(rankingRef, {
        displayName, // Always update display name from profile
        weeklyScore: current.weeklyScore + scoreToAdd,
        dailyPoints: current.dailyPoints + pointsToAdd,
        totalGames: current.totalGames + 1,
        lastUpdated: serverTimestamp(),
      })
    } else {
      const newRanking: RoomRanking = {
        userId,
        displayName,
        weeklyScore: scoreToAdd,
        dailyPoints: pointsToAdd,
        totalGames: 1,
        lastUpdated: Timestamp.now(),
      }
      await setDoc(rankingRef, newRanking)
    }
  } catch (error) {
    console.error('Error updating room ranking:', error)
    throw error
  }
}

/**
 * Vote for a user in a room (daily points)
 */
export async function voteForUser(
  roomId: string,
  voterId: string,
  votedForId: string
): Promise<boolean> {
  try {
    const today = new Date().toISOString().split('T')[0]
    const voteId = `${voterId}_${today}`
    const voteRef = doc(db, 'rooms', roomId, 'votes', voteId)

    // Check if user already voted today
    const voteSnap = await getDoc(voteRef)
    if (voteSnap.exists()) {
      return false // Already voted today
    }

    // Create vote
    const vote: DailyVote = {
      voterId,
      votedForId,
      date: today,
      createdAt: Timestamp.now(),
    }

    await setDoc(voteRef, vote)

    // Update the voted user's daily points
    const rankingRef = doc(db, 'rooms', roomId, 'rankings', votedForId)
    const rankingSnap = await getDoc(rankingRef)

    if (rankingSnap.exists()) {
      const current = rankingSnap.data() as RoomRanking
      await updateDoc(rankingRef, {
        dailyPoints: current.dailyPoints + 1,
        lastUpdated: serverTimestamp(),
      })
    }

    return true
  } catch (error) {
    console.error('Error voting for user:', error)
    throw error
  }
}

/**
 * Check if user has voted today in a room
 */
export async function hasVotedToday(
  roomId: string,
  userId: string
): Promise<boolean> {
  try {
    const today = new Date().toISOString().split('T')[0]
    const voteId = `${userId}_${today}`
    const voteRef = doc(db, 'rooms', roomId, 'votes', voteId)

    const voteSnap = await getDoc(voteRef)
    return voteSnap.exists()
  } catch (error) {
    console.error('Error checking vote status:', error)
    return false
  }
}

/**
 * Get room members with their display names
 */
export async function getRoomMembers(roomId: string): Promise<RoomMember[]> {
  try {
    const roomRef = doc(db, 'rooms', roomId)
    const roomSnap = await getDoc(roomRef)

    if (!roomSnap.exists()) {
      return []
    }

    const room = roomSnap.data() as Room
    const memberIds = room.members || []

    // Fetch display names for all members
    const members: RoomMember[] = await Promise.all(
      memberIds.map(async (memberId) => {
        const profileRef = doc(db, 'profiles', memberId)
        const profileSnap = await getDoc(profileRef)
        const displayName = profileSnap.exists()
          ? profileSnap.data()?.displayName || 'Anonim'
          : 'Anonim'

        return {
          userId: memberId,
          displayName,
          joinedAt: room.createdAt, // We don't track individual join times yet
        }
      })
    )

    return members
  } catch (error) {
    console.error('Error getting room members:', error)
    return []
  }
}
